import React from 'react';

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          <div className="bg-gradient-to-r from-tal-brown-600 to-tal-blue-600 px-8 py-12 text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
            <p className="text-lg opacity-90">End User License Agreement</p>
            <p className="text-sm mt-4 opacity-75">Effective Date: January 1, 2025</p>
          </div>

          <div className="px-8 py-10 space-y-8">
            {/* Introduction */}
            <div className="bg-tal-blue-50 border-l-4 border-tal-blue-500 p-6 rounded-r">
              <p className="text-gray-800 leading-relaxed">
                <strong>PLEASE READ THESE TERMS CAREFULLY.</strong> By accessing or using Tales and Legends services,
                websites, or games (collectively, the <strong>"Services"</strong>), you acknowledge that you have read,
                understood, and agree to be bound by these Terms of Service (the <strong>"Terms"</strong>). If you do not
                agree to these Terms, you may not access or use the Services.
              </p>
            </div>

            {/* Section 1: Access and Use */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                1. Access and Use of Services
              </h2>

              <div className="space-y-4 ml-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">A. Eligibility</h3>
                  <p className="text-gray-700 leading-relaxed">
                    You must be at least 13 years of age to use the Services. If you are under 18 years of age (or the age
                    of legal majority in your jurisdiction), you must have your parent or legal guardian's permission to use
                    the Services. By using the Services, you represent and warrant that you meet these eligibility requirements.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">B. License Grant</h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Subject to your compliance with these Terms, Tales and Legends grants you a limited, non-exclusive,
                    non-transferable, non-sublicensable, revocable license to access and use the Services for your personal,
                    non-commercial entertainment purposes. You may not:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>Copy, modify, or create derivative works of the Services</li>
                    <li>Reverse engineer, decompile, or disassemble any aspect of the Services</li>
                    <li>Sell, rent, lease, sublicense, or otherwise transfer the Services</li>
                    <li>Remove or alter any proprietary notices or labels</li>
                    <li>Use the Services for any commercial purpose without our prior written consent</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">C. Account Creation</h3>
                  <p className="text-gray-700 leading-relaxed">
                    You may need to create an account to access certain features. You are responsible for maintaining the
                    confidentiality of your account credentials and for all activities under your account. You agree to notify
                    us immediately of any unauthorized use of your account.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 2: User Content and Conduct */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                2. User Content and Acceptable Use
              </h2>

              <div className="space-y-4 ml-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">A. User Submissions</h3>
                  <p className="text-gray-700 leading-relaxed">
                    The Services may allow you to submit content, including text, images, and other materials
                    (<strong>"User Content"</strong>). You retain ownership of your User Content, but you grant Tales and
                    Legends a worldwide, perpetual, irrevocable, royalty-free license to use, reproduce, modify, publish,
                    and distribute your User Content in connection with the Services.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">B. Prohibited Conduct</h3>
                  <p className="text-gray-700 leading-relaxed mb-3">You agree not to:</p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>Violate any applicable laws or regulations</li>
                    <li>Infringe upon the rights of others, including intellectual property rights</li>
                    <li>Upload viruses or malicious code</li>
                    <li>Harass, abuse, or harm other users</li>
                    <li>Engage in fraudulent or deceptive practices</li>
                    <li>Use automated systems or bots to access the Services</li>
                    <li>Interfere with or disrupt the Services or servers</li>
                    <li>Use the Services to train artificial intelligence or machine learning models without our consent</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 3: Intellectual Property */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                3. Intellectual Property Rights
              </h2>
              <p className="text-gray-700 leading-relaxed ml-4">
                The Services and all content, features, and functionality are owned by Tales and Legends or its licensors
                and are protected by copyright, trademark, and other intellectual property laws. These Terms grant you a
                license to use the Services, not a sale. All rights not expressly granted are reserved by Tales and Legends.
              </p>
            </section>

            {/* Section 4: Purchases and Virtual Items */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                4. Purchases and Virtual Items
              </h2>

              <div className="space-y-4 ml-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">A. In-App Purchases</h3>
                  <p className="text-gray-700 leading-relaxed">
                    The Services may offer virtual items, currency, or other digital goods (<strong>"Virtual Items"</strong>)
                    for purchase. All purchases are processed through third-party platforms (such as Apple App Store or Google
                    Play Store) and are subject to their terms and policies.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">B. Virtual Items Terms</h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Virtual Items have no monetary value and cannot be redeemed for cash. Virtual Items are non-transferable
                    and non-refundable except as required by law. Tales and Legends may modify, suspend, or terminate Virtual
                    Items at any time without liability.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 5: DMCA */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                5. Copyright and DMCA
              </h2>
              <p className="text-gray-700 leading-relaxed ml-4 mb-3">
                We respect intellectual property rights. If you believe content on the Services infringes your copyright,
                please notify us in writing with the following information:
              </p>
              <ul className="list-disc pl-10 space-y-2 text-gray-700">
                <li>Identification of the copyrighted work claimed to be infringed</li>
                <li>Identification of the infringing material and its location</li>
                <li>Your contact information</li>
                <li>A statement that you have a good faith belief that use is not authorized</li>
                <li>A statement that the information is accurate and you are authorized to act</li>
                <li>Your physical or electronic signature</li>
              </ul>
            </section>

            {/* Section 6: Termination */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                6. Term and Termination
              </h2>
              <p className="text-gray-700 leading-relaxed ml-4">
                These Terms remain in effect until terminated. We may suspend or terminate your access to the Services at
                any time, with or without cause or notice. You may terminate these Terms by ceasing to use the Services and
                deleting your account. Upon termination, all licenses granted herein will immediately cease. Sections 3, 7,
                8, 9, 10, 11, and 12 will survive any termination.
              </p>
            </section>

            {/* Section 7: Indemnification */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                7. Indemnification
              </h2>
              <p className="text-gray-700 leading-relaxed ml-4">
                You agree to indemnify, defend, and hold harmless Tales and Legends, its affiliates, and their respective
                officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses,
                including reasonable attorneys' fees, arising out of or in any way connected with your access to or use of
                the Services, your User Content, or your violation of these Terms.
              </p>
            </section>

            {/* Section 8: Disclaimer */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                8. Disclaimer of Warranties
              </h2>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r ml-4">
                <p className="text-gray-800 leading-relaxed font-semibold">
                  THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED,
                  INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT,
                  OR THAT THE SERVICES WILL BE UNINTERRUPTED OR ERROR-FREE. TALES AND LEGENDS DOES NOT WARRANT THAT THE SERVICES
                  WILL MEET YOUR REQUIREMENTS OR THAT ANY DEFECTS WILL BE CORRECTED.
                </p>
              </div>
            </section>

            {/* Section 9: Limitation of Liability */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                9. Limitation of Liability
              </h2>
              <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r ml-4">
                <p className="text-gray-800 leading-relaxed font-semibold mb-4">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, TALES AND LEGENDS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
                  SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY
                  OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
                </p>
                <p className="text-gray-800 leading-relaxed">
                  IN NO EVENT SHALL TALES AND LEGENDS' TOTAL LIABILITY TO YOU EXCEED THE GREATER OF (A) THE AMOUNT YOU HAVE
                  PAID TO TALES AND LEGENDS IN THE TWELVE (12) MONTHS PRIOR TO THE EVENT GIVING RISE TO LIABILITY, OR (B) $100.
                </p>
              </div>
            </section>

            {/* Section 10: Governing Law */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                10. Governing Law and Dispute Resolution
              </h2>
              <div className="space-y-4 ml-4">
                <p className="text-gray-700 leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of the United States and the
                  State of California, without regard to its conflict of law provisions.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Any disputes arising out of or relating to these Terms or the Services shall be resolved through binding
                  arbitration in accordance with the rules of the American Arbitration Association, except that either party
                  may seek injunctive relief in court. You and Tales and Legends each waive the right to a jury trial.
                </p>
              </div>
            </section>

            {/* Section 11: Changes to Terms */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                11. Changes to These Terms
              </h2>
              <p className="text-gray-700 leading-relaxed ml-4">
                We reserve the right to modify these Terms at any time. We will provide notice of material changes by posting
                the updated Terms on our website or within the Services. Your continued use of the Services after such changes
                constitutes your acceptance of the new Terms.
              </p>
            </section>

            {/* Section 12: Miscellaneous */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-tal-brown-200">
                12. Miscellaneous
              </h2>
              <div className="space-y-4 ml-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">A. Entire Agreement</h3>
                  <p className="text-gray-700 leading-relaxed">
                    These Terms constitute the entire agreement between you and Tales and Legends regarding the Services.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">B. Severability</h3>
                  <p className="text-gray-700 leading-relaxed">
                    If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in
                    full force and effect.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">C. Assignment</h3>
                  <p className="text-gray-700 leading-relaxed">
                    You may not assign these Terms without our prior written consent. We may assign these Terms at any time
                    without notice.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">D. Export Control</h3>
                  <p className="text-gray-700 leading-relaxed">
                    You agree to comply with all applicable export and import laws and regulations in connection with your
                    use of the Services.
                  </p>
                </div>
              </div>
            </section>

            {/* Contact Information */}
            <section className="bg-gray-50 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-2">
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="text-gray-700 space-y-1">
                <p className="font-semibold">Tales and Legends</p>
                <p>Email: legal@tales-and-legends.com</p>
                <p>Website: www.tales-and-legends.com</p>
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="bg-gray-100 px-8 py-6 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-center text-sm text-gray-600">
              <p>Last Updated: January 1, 2025</p>
              <p className="mt-2 sm:mt-0">© {new Date().getFullYear()} Tales and Legends. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
